import React, { useState, useEffect } from "react";
import { DateRangePicker } from 'rsuite';
import "rsuite/dist/rsuite.css";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import moment from 'moment';
import Select from 'react-select';
import { useStoreBrnDSSelector } from "../store/selectors/userSelector";
import { useDispatch } from "react-redux";
import { ACTIONS } from "../_config";
import { useFilterSelector } from "../store/selectors/filterSelector";

interface FilterProps {
    onApplyFilter: (filters: any) => void;
    onClearFilter: () => void;
    filterClass: string;
}

const Filter: React.FC<FilterProps> = ({ onApplyFilter, onClearFilter, filterClass }) => {
    const dispatch = useDispatch();
    const savedFilter = useFilterSelector();
    const [isSmallScreen, setIsSmallScreen] = useState<boolean>(() =>
        typeof window !== 'undefined' ? window.innerWidth <= 767 : false
    );

    const [source, setSource] = useState(savedFilter.source || '');
    const [keyword, setKeyword] = useState(savedFilter.keyword || '');
    const [startDate, setStartDate] = useState<string | null>(savedFilter.startDate);
    const [endDate, setEndDate] = useState<string | null>(savedFilter.endDate);
    const [minValue, setMinValue] = useState(savedFilter.minRating ?? 1);
    const [maxValue, setMaxValue] = useState(savedFilter.maxRating ?? 5);

    // Sync local state when Redux filter changes (e.g. on first mount after tab switch)
    useEffect(() => {
        setSource(savedFilter.source || '');
        setKeyword(savedFilter.keyword || '');
        setStartDate(savedFilter.startDate);
        setEndDate(savedFilter.endDate);
        setMinValue(savedFilter.minRating ?? 1);
        setMaxValue(savedFilter.maxRating ?? 5);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= 767);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleDateChange = (dateRange: any | Date) => {
        if (dateRange) {
            const [start, end] = dateRange;
            setStartDate(moment(start).format('YYYY-MM-DD'));
            setEndDate(moment(end).format('YYYY-MM-DD'));
        }
    };

    const applyFilter = () => {
        let filter: any = {}
        if (source && source !== 'all') {
            filter.source = source;
        }
        if (keyword) {
            filter.keyword = keyword;
        }
        if (startDate) {
            filter.start_date = startDate;
        }
        if (endDate) {
            filter.end_date = endDate;
        }
        if (maxValue && minValue) {
            filter.max_rating = maxValue;
            filter.min_rating = minValue;
        }

        dispatch({
            type: ACTIONS.FILTER.SET,
            payload: {
                source,
                keyword,
                startDate,
                endDate,
                minRating: minValue,
                maxRating: maxValue,
                applied: filter,
            },
        });

        onApplyFilter(filter);
    };

    const clearFilter = () => {
        const today = moment().format('YYYY-MM-DD');
        setSource('');
        setKeyword('');
        setStartDate(today);
        setEndDate(today);
        setMaxValue(5);
        setMinValue(1);

        dispatch({
            type: ACTIONS.FILTER.SET,
            payload: {
                source: '',
                keyword: '',
                startDate: today,
                endDate: today,
                minRating: 1,
                maxRating: 5,
                applied: { start_date: today, end_date: today, min_rating: 1, max_rating: 5 },
            },
        });

        onClearFilter();
    };

    const marks = {
        1: '1',
        2: '2',
        3: '3',
        4: '4',
        5: '5',
    };

    return (
        <div className={filterClass}>
            <div className="border-b mb-4">
                <div
                    className="w-full text-left py-3 px-4 bg-gray-200 rounded-md text-gray-600 font-semibold flex justify-between items-center"
                >
                    Filter
                    <span className="ml-2">{useStoreBrnDSSelector()?.branch_name}</span>
                </div>

                {/*-----------------filter-accordion----------------- */}
                <div className="p-4 bg-gray-50 max-h-auto">
                        {/* Source Filter (Single Row) */}
                        <div className="mb-5">
                            <label className="block text-sm text-gray-600 mb-2">Source</label>
                            <Select
                                options={[
                                    { value: 'all', label: 'All' },
                                    { value: 'yelp', label: 'Yelp' },
                                    { value: 'csv', label: 'CSV' },
                                ]}
                                value={{ value: source || 'all', label: source === 'yelp' ? 'Yelp' : source === 'csv' ? 'CSV' : 'All' }}
                                onChange={(option: any) => setSource(option?.value === 'all' ? '' : option?.value || '')}
                                isSearchable={false}
                                styles={{
                                    control: (base, state) => ({
                                        ...base,
                                        borderRadius: '6px',
                                        borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                                        borderWidth: '1px',
                                        boxShadow: 'none',
                                        '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#d1d5db' },
                                        minHeight: '36px',
                                        fontSize: '14px',
                                        backgroundColor: 'white',
                                    }),
                                    placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '14px' }),
                                    option: (base, state) => ({
                                        ...base,
                                        color: state.isSelected ? 'white' : '#6b7280',
                                        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
                                        fontSize: '14px',
                                    }),
                                    singleValue: (base) => ({ ...base, color: '#111827', fontSize: '14px' }),
                                }}
                            />
                        </div>

                        {/* Date Range Filter (Single Row) */}
                        <div className="mb-5">
                            <label className="block text-sm text-gray-600">Date Range</label>
                            <DateRangePicker
                                className="block-lg w-full rounded-md"
                                onChange={handleDateChange}
                                format="yyyy-MM-dd"
                                showOneCalendar
                                placement={isSmallScreen ? "autoVerticalStart" : "left"}
                                menuClassName="filter-date-range-menu"
                                value={[startDate ? new Date(startDate) : new Date(), endDate ? new Date(endDate) : new Date()]}
                            />
                        </div>

                        {/* Rating Range Filter */}
                        <div className="mb-5">
                            <label className="block text-sm text-gray-600 mb-3">Rating</label>
                            <Slider
                                range
                                min={1}
                                max={5}
                                step={1}
                                value={[minValue, maxValue]}
                                onChange={(value: number | number[]) => {
                                    if (Array.isArray(value)) {
                                        setMinValue(value[0]);
                                        setMaxValue(value[1]);
                                    }
                                }}
                                marks={marks}
                                railStyle={{
                                    backgroundColor: '#ddd',
                                    height: '6px',
                                    borderRadius: '3px',
                                }}
                                trackStyle={{
                                    backgroundColor: '#4f46e5',
                                    height: '6px',
                                    borderRadius: '3px',
                                }}
                                handleStyle={{
                                    backgroundColor: '#4f46e5',
                                    border: '2px solid white',
                                    height: '16px',
                                    width: '16px',
                                    borderRadius: '50%',
                                }}
                            />
                        </div>

                        {/* Apply / Clear Buttons */}
                        <div className="flex space-x-4 mt-12">
                            <button
                                onClick={applyFilter}
                                className="py-2 px-3 bg-blue-600 rounded-md text-white font-semibold w-full"
                            >
                                Apply
                            </button>
                            <button
                                onClick={clearFilter}
                                className="py-2 px-3 bg-red-500 rounded-md text-white font-semibold w-full"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
            </div>
        </div>
    );
};

export default Filter;
